import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/Eyebrow";
import { api } from "@/lib/api";
import { ArrowLeft } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/blog/posts/${slug}`);
        setPost(res.data);
      } catch (e) {
        setError(e?.response?.status === 404 ? "not_found" : "load_error");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  return (
    <Layout>
      {post && (
        <SEO
          title={post.title}
          description={post.excerpt || (post.body_markdown || "").slice(0, 160)}
          canonical={`/blog/${slug}`}
          ogType="article"
          ogImage={post.hero_image_url}
          author={post.author}
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            image: post.hero_image_url ? [post.hero_image_url] : undefined,
            datePublished: post.published_at,
            author: { "@type": "Person", name: post.author || "Taimur Ahmed" },
            publisher: {
              "@type": "Organization",
              name: "Beyond Valencia",
              logo: { "@type": "ImageObject", url: "https://beyondvalencia.com/logo.png" },
            },
            mainEntityOfPage: `https://beyondvalencia.com/blog/${slug}`,
          }}
        />
      )}
      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-28" data-testid="blog-post-page">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-brass hover:text-ivy transition-colors mb-10"
        >
          <ArrowLeft className="h-3 w-3" /> All field notes
        </Link>

        {loading && <div className="text-ink-soft">Loading…</div>}

        {error === "not_found" && (
          <div data-testid="blog-post-not-found">
            <Eyebrow>404</Eyebrow>
            <h1 className="font-serif font-medium text-3xl text-ink mt-3">Post not found.</h1>
            <p className="text-ink-soft mt-3">It may have been moved, or the link is stale.</p>
          </div>
        )}

        {post && (
          <>
            <div className="text-[10px] uppercase tracking-[0.24em] text-brass">
              {new Date(post.published_at).toLocaleDateString(undefined, {
                month: "long", day: "numeric", year: "numeric",
              })} · {post.author}
            </div>
            <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[3.75rem] text-ink leading-[1.05] tracking-tightest mt-4">
              {post.title}
            </h1>
            <p className="mt-6 text-ink-soft text-lg leading-relaxed border-l-2 border-brass pl-5">
              {post.excerpt}
            </p>
            {post.hero_image_url && (
              <div className="mt-12 border border-stone/40 p-2 bg-bone-dim/40">
                <img
                  src={post.hero_image_url}
                  alt={post.title}
                  className="w-full aspect-[16/9] object-cover"
                />
              </div>
            )}
            <div className="prose-editorial mt-12" data-testid="blog-post-body">
              <ReactMarkdown>{post.body_markdown}</ReactMarkdown>
            </div>
          </>
        )}
      </article>
    </Layout>
  );
}
